import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { ArrowLeft, X, Image as ImageIcon } from "lucide-react";

const CONDITION_CLASS = {
  excellent: "text-emerald-400", good: "text-[color:var(--gold)]",
  needs_attention: "text-[color:var(--warning)]", critical: "text-[color:var(--error)]",
};

export default function DisplayPhotos() {
  const [zones, setZones] = useState([]);
  const [fandoms, setFandoms] = useState([]);
  const [zoneId, setZoneId] = useState("");
  const [fandomId, setFandomId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [photos, setPhotos] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/zones"), api.get("/fandoms")])
      .then(([z, f]) => { setZones(z.data); setFandoms(f.data); })
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (zoneId) q.set("zone_id", zoneId);
    if (fandomId) q.set("fandom_id", fandomId);
    if (start) q.set("start", start);
    if (end) q.set("end", end);
    q.set("limit", "120");
    api.get(`/display_photos?${q.toString()}`).then(({ data }) => setPhotos(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [zoneId, fandomId, start, end]);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const p of photos) {
      const key = `${p.zone_id}::${(p.fandom_names || []).join(",")}`;
      if (!map.has(key)) {
        map.set(key, { zone_name: p.zone_name, fandom_names: p.fandom_names, photos: [] });
      }
      map.get(key).photos.push(p);
    }
    return Array.from(map.values());
  }, [photos]);

  return (
    <div className="space-y-6" data-testid="display-photos">
      <Link to="/display" className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)] text-sm"><ArrowLeft size={14}/> Back to Dashboard</Link>

      <div>
        <div className="label-caps">History</div>
        <h1 className="font-heading text-3xl sm:text-4xl">Photo History</h1>
        <div className="text-sm text-[color:var(--text-secondary)] mt-1">{photos.length} photo{photos.length === 1 ? "" : "s"} · compare how displays change over time</div>
      </div>

      <div className="hos-card p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="label-caps">Zone</label>
          <select className="hos-input" value={zoneId} onChange={(e) => setZoneId(e.target.value)} data-testid="ph-zone">
            <option value="">All zones</option>
            {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-caps">Fandom</label>
          <select className="hos-input" value={fandomId} onChange={(e) => setFandomId(e.target.value)} data-testid="ph-fandom">
            <option value="">All fandoms</option>
            {fandoms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-caps">From</label>
          <input type="date" className="hos-input" value={start} onChange={(e) => setStart(e.target.value)}/>
        </div>
        <div>
          <label className="label-caps">To</label>
          <input type="date" className="hos-input" value={end} onChange={(e) => setEnd(e.target.value)}/>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-[color:var(--text-secondary)] py-8">Loading…</div>
      ) : grouped.length === 0 ? (
        <div className="hos-card p-8 text-center text-[color:var(--text-secondary)]">
          <ImageIcon size={32} className="mx-auto mb-2 opacity-40"/>
          No photos match these filters yet.
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map((g, gi) => (
            <div key={gi} className="hos-card p-4">
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
                <h2 className="font-heading text-xl">{g.zone_name}</h2>
                <div className="text-sm text-[color:var(--text-secondary)]">
                  {(g.fandom_names || []).join(" · ") || "—"}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {g.photos.map((p) => (
                  <button key={p.photo_id} onClick={() => setPreview(p)} className="text-left" data-testid={`ph-${p.photo_id}`}>
                    <div className="aspect-square rounded overflow-hidden border border-[color:var(--border)] hover:border-[color:var(--gold)]">
                      <AuthImage photo={p} className="w-full h-full object-cover"/>
                    </div>
                    <div className="text-[10px] text-[color:var(--text-secondary)] mt-1 flex items-center justify-between">
                      <span>{p.inspection_date}</span>
                      {p.condition && <span className={CONDITION_CLASS[p.condition]}>{p.condition}</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4" onClick={() => setPreview(null)}>
          <AuthImage photo={preview} alt="" className="max-h-[80vh] max-w-[95vw] rounded" onClick={(e) => e.stopPropagation()}/>
          <div className="mt-3 text-xs text-white text-center">
            {preview.zone_name} · {(preview.fandom_names || []).join(", ")} · {preview.inspection_date} · {preview.manager_name}
            {" · "}<Link to={`/display/${preview.check_id}`} className="text-[color:var(--gold)] underline">{preview.check_number}</Link>
          </div>
          <button className="absolute top-4 right-4 text-white" onClick={() => setPreview(null)}><X size={28}/></button>
        </div>
      )}
    </div>
  );
}
